import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import '../styles/Orders.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruck, faArrowRight, faBox, faSearch, faExternalLinkAlt, faTag, faPercent, faUndo, faExchangeAlt, faShoppingBag, faInfoCircle, faUpload, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Modal, Button, Form, Row, Col, Badge, Spinner, Alert } from 'react-bootstrap';

const Orders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const { user } = useAuth();
    
    // İade talebi için state'ler
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [returnReason, setReturnReason] = useState('');
    const [returnDescription, setReturnDescription] = useState('');
    const [returnProcessing, setReturnProcessing] = useState(false);
    const [returnImages, setReturnImages] = useState([]);
    const [previewImages, setPreviewImages] = useState([]);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    useEffect(() => {
        if (!user) {
            navigate('/');
            return;
        }

        const fetchOrders = async () => {
            try {
                // Kullanıcının cust_id'si ve email'i ile siparişleri getir
                const response = await api.get(`/orders/user/${user.cust_id}`);
                
                // API yanıtını detaylı olarak logla
                console.log("API Response (Full):", JSON.stringify(response.data, null, 2));
                
                // Sipariş verilerini işle
                const processedOrders = response.data.map(order => {
                    // Ürün bilgilerini kontrol et
                    if (order.items && order.items.length > 0) {
                        console.log(`Order ${order.orderNumber} - Items:`, JSON.stringify(order.items, null, 2));
                    } else {
                        console.warn(`Order ${order.orderNumber} - No items found`);
                    }
                    
                    // İndirim bilgilerini kontrol et
                    if (order.discount) {
                        console.log(`Order ${order.orderNumber} - Discount:`, JSON.stringify(order.discount, null, 2));
                    } else {
                        console.warn(`Order ${order.orderNumber} - No discount found`);
                    }
                    
                    // Backend'den gelen verileri doğrudan kullan
                    return order;
                });
                
                // Siparişleri tarihe göre sırala (en yeni en üstte)
                const sortedOrders = processedOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                
                setOrders(sortedOrders);
            } catch (err) {
                console.error("API Error:", err);
                setError(err.response?.data?.error || 'Siparişler getirilemedi');
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, [user, navigate]);

    // Sipariş tarihinden itibaren 14 gün geçip geçmediğini kontrol et
    const isOrderReturnEligible = (orderDate) => {
        const orderDateTime = new Date(orderDate);
        const currentDateTime = new Date();
        const differenceInTime = currentDateTime.getTime() - orderDateTime.getTime();
        const differenceInDays = differenceInTime / (1000 * 3600 * 24);
        return differenceInDays <= 14;
    };

    // İade talebi modalını aç
    const handleReturnRequest = (order) => {
        // Ödeme durumunu kontrol et
        if (order.paymentStatus !== 'success') {
            alert('Bu sipariş için ödeme tamamlanmadığından iade talebi oluşturamazsınız.');
            return;
        }
        
        // Sipariş tarihinden itibaren 14 gün geçip geçmediğini kontrol et
        if (!isOrderReturnEligible(order.createdAt)) {
            // 14 gün geçtiyse uyarı göster
            alert('Bu sipariş için iade süresi dolmuştur (14 gün). İade talebi oluşturamazsınız.');
            return;
        }
        
        setSelectedOrder(order);
        setReturnReason('');
        setReturnDescription('');
        setReturnImages([]);
        setPreviewImages([]);
        setShowReturnModal(true);
    };

    // Siparişin aktif bir iade talebi olup olmadığını kontrol et
    const hasActiveReturnRequest = (order) => {
        // Eğer sipariş için hiç iade talebi yoksa veya iade talebi reddedilmiş/iptal edilmişse false döner
        return order.return_requested === true && 
               order.return_status && 
               !['rejected', 'cancelled'].includes(order.return_status);
    };

    // Görsel yükleme işlemi
    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        
        // Maksimum 5 görsel kontrolü
        if (returnImages.length + files.length > 5) {
            alert('En fazla 5 görsel yükleyebilirsiniz.');
            return;
        }
        
        // Dosya boyutu kontrolü (her dosya maksimum 5MB)
        const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
        if (oversizedFiles.length > 0) {
            alert('Yüklenen görseller 5MB\'dan küçük olmalıdır.');
            return;
        }
        
        // Dosya türü kontrolü
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
        const invalidFiles = files.filter(file => !validTypes.includes(file.type));
        if (invalidFiles.length > 0) {
            alert('Sadece JPEG, PNG ve WebP formatında görseller yükleyebilirsiniz.');
            return;
        }
        
        // Yeni görselleri ekle
        setReturnImages(prevImages => [...prevImages, ...files]);
        
        // Önizleme URL'lerini oluştur
        const newPreviewImages = files.map(file => ({
            file,
            url: URL.createObjectURL(file)
        }));
        
        setPreviewImages(prevPreviews => [...prevPreviews, ...newPreviewImages]);
    };

    // Görsel kaldırma işlemi
    const removeImage = (index) => {
        // Önizleme URL'sini temizle
        URL.revokeObjectURL(previewImages[index].url);
        
        // Dizilerden kaldır
        setPreviewImages(prevPreviews => prevPreviews.filter((_, i) => i !== index));
        setReturnImages(prevImages => prevImages.filter((_, i) => i !== index));
    };

    // İade talebini gönder
    const submitReturnRequest = async () => {
        if (!returnReason) {
            alert('Lütfen bir iade nedeni seçin');
            return;
        }

        setReturnProcessing(true);
        
        try {
            // Form verilerini oluştur
            const formData = new FormData();
            formData.append('orderId', selectedOrder.id);
            formData.append('orderNumber', selectedOrder.orderNumber);
            formData.append('reason', returnReason);
            formData.append('description', returnDescription);
            
            // Görselleri ekle
            returnImages.forEach(image => {
                formData.append('images', image);
            });
            
            // İade talebini gönder
            const response = await api.post('/returns/create', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            // Başarılı ise
            if (response.status === 201) {
                // Siparişi güncelle
                const updatedOrders = orders.map(order => 
                    order.id === selectedOrder.id 
                        ? { ...order, return_requested: true } 
                        : order
                );
                setOrders(updatedOrders);
                
                // Modalı kapat
                setShowReturnModal(false);
                
                // Başarılı iade talebi modalını göster
                setShowSuccessModal(true);
            }
        } catch (err) {
            console.error('İade talebi oluşturma hatası:', err);
            alert(err.response?.data?.error || 'İade talebi oluşturulurken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        } finally {
            setReturnProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status" variant="primary" />
                <p className="mt-3">Siparişleriniz yükleniyor...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger" className="m-3">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                {error}
            </Alert>
        );
    }

    const getStatusClass = (status) => {
        switch (status) {
            case 'processing':
                return 'status-processing';
            case 'completed':
                return 'status-completed';
            case 'failed':
                return 'status-failed';
            default:
                return '';
        }
    };

    const getPaymentStatusClass = (status) => {
        switch (status) {
            case 'success':
                return 'payment-success';
            case 'pending':
                return 'payment-pending';
            case 'failed':
                return 'payment-failed';
            default:
                return '';
        }
    };

    const getPaymentStatusText = (status) => {
        switch (status) {
            case 'success':
                return 'Başarılı';
            case 'pending':
                return 'Beklemede';
            case 'failed':
                return 'Başarısız';
            default:
                return status;
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'processing':
                return 'İşleniyor';
            case 'completed':
                return 'Tamamlandı';
            case 'pending':
                return 'Beklemede';
            case 'failed':
                return 'Başarısız';
            default:
                return status;
        }
    };

    // Güvenli bir şekilde toLocaleString kullan
    const formatPrice = (price) => {
        if (typeof price === 'number') {
            return price.toLocaleString('tr-TR');
        }
        return '0';
    };

    return (
        <div className="orders-container">
            <div className="orders-header">
                <h1>Siparişlerim</h1>
                <Button 
                    variant="outline-primary" 
                    onClick={() => navigate('/return-requests')}
                    className="view-returns-btn"
                >
                    <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />
                    İade Taleplerim
                </Button>
            </div>
            {orders.length === 0 ? (
                <div className="no-orders">
                    <p>Henüz siparişiniz bulunmamaktadır.</p>
                </div>
            ) : (
                <div className="orders-list">
                    {orders.map(order => (
                        <div key={order.id} className="order-card">
                            <div className="order-header">
                                <h3>{order.orderNumber}</h3>
                                <span className={`status-badge ${getStatusClass(order.status)}`}>
                                    {getStatusText(order.status)}
                                </span>
                                <span className="order-date">
                                    {new Date(order.createdAt).toLocaleDateString('tr-TR', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </span>
                            </div>
                            
                            <div className="order-items">
                                {order.items && order.items.length > 0 ? (
                                    order.items.map((item, index) => (
                                        <div key={index} className="order-item">
                                            <div className="item-image">
                                                {item.image && (
                                                    <img src={item.image} alt={item.name} />
                                                )}
                                            </div>
                                            <div className="item-details">
                                                <h4>{item.name}</h4>
                                                {/* Telefon modeli bilgisini göster */}
                                                {item.model_name && (
                                                    <p className="model-info">
                                                        {item.model_name}
                                                    </p>
                                                )}
                                                {/* Renk bilgisini göster */}
                                                {item.color_name && (
                                                    <p className="color-info">
                                                        Renk: {item.color_name}
                                                    </p>
                                                )}
                                                <div className="price-quantity">
                                                    <span className="price">{formatPrice(item.price)} TL</span>
                                                    <span className="quantity">Adet: {item.quantity}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="no-items-message">Ürün detayları mevcut değil</p>
                                )}
                            </div>

                            <div className="order-details">
                                <p>
                                    <span>Toplam Tutar:</span>
                                    <span>{formatPrice(order.totalAmount)} TL</span>
                                </p>
                                <p>
                                    <span>Ödeme Durumu:</span>
                                    <span className={`status-badge ${getPaymentStatusClass(order.paymentStatus)}`}>
                                        {getPaymentStatusText(order.paymentStatus)}
                                    </span>
                                </p>
                                
                                {/* İndirim bilgisi */}
                                {order.discount && (
                                    <p className="discount-info">
                                        <span>
                                            <FontAwesomeIcon icon={faTag} className="discount-icon" /> 
                                            İndirim:
                                        </span>
                                        <span className="discount-details">
                                            {order.discount.code} 
                                            {order.discount.percentage && (
                                                <span>({order.discount.percentage}%)</span>
                                            )}
                                            <span className="discount-amount">-{formatPrice(order.discount.amount)} TL</span>
                                        </span>
                                    </p>
                                )}
                                
                                {/* İade Talebi Butonu */}
                                <div className="order-actions">
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleReturnRequest(order)}
                                        disabled={
                                            !isOrderReturnEligible(order.createdAt) || 
                                            hasActiveReturnRequest(order) ||
                                            order.paymentStatus !== 'success'
                                        }
                                    >
                                        <FontAwesomeIcon icon={faUndo} className="return-icon" />
                                        İade Talebi
                                    </Button>
                                    {!isOrderReturnEligible(order.createdAt) && (
                                        <small className="return-info">İade süresi dolmuştur (14 gün).</small>
                                    )}
                                    {hasActiveReturnRequest(order) && (
                                        <small className="return-info">Bu sipariş için iade talebiniz bulunmaktadır.</small>
                                    )}
                                    {order.paymentStatus !== 'success' && (
                                        <small className="return-info">Ödeme tamamlanmadığı için iade talebi oluşturamazsınız.</small>
                                    )}
                                </div>
                            </div>

                            {order.shipments && Array.isArray(order.shipments) && order.shipments.length > 0 && (
                                <div className="shipping-details">
                                    <h4>
                                        <FontAwesomeIcon icon={faTruck} className="shipping-icon" />
                                        Kargo Bilgileri
                                    </h4>
                                    <div className="shipping-info-grid">
                                        <div className="shipping-info-item">
                                            <span className="label">Kargo Firması:</span>
                                            <span className="value">{order.shipments[0].provider_code || 'Belirtilmemiş'}</span>
                                        </div>
                                        
                                        {order.shipments[0].barcode && (
                                            <div className="shipping-info-item">
                                                <span className="label">Barkod:</span>
                                                <span className="value">{order.shipments[0].barcode}</span>
                                            </div>
                                        )}
                                        
                                        {order.shipments[0].geliver_shipment_id && (
                                            <div className="shipping-info-item">
                                                <span className="label">Kargo Takip:</span>
                                                <a 
                                                    href={`https://app.geliver.io/tracking/${order.shipments[0].geliver_shipment_id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="tracking-link"
                                                >
                                                    <FontAwesomeIcon icon={faSearch} className="tracking-icon" />
                                                    Kargo Durumunu Takip Et
                                                    <FontAwesomeIcon icon={faExternalLinkAlt} className="external-link-icon" />
                                                </a>
                                            </div>
                                        )}
                                        
                                        {order.shipments[0].label_url && (
                                            <div className="shipping-info-item">
                                                <span className="label">Kargo Etiketi:</span>
                                                <a 
                                                    href={order.shipments[0].label_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="label-link"
                                                >
                                                    <FontAwesomeIcon icon={faBox} className="label-icon" />
                                                    Kargo Etiketini Görüntüle
                                                    <FontAwesomeIcon icon={faExternalLinkAlt} className="external-link-icon" />
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* İade Talebi Modalı */}
            <Modal show={showReturnModal} onHide={() => setShowReturnModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>İade Talebi</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedOrder && (
                        <>
                            <p><strong>Sipariş No:</strong> {selectedOrder.orderNumber}</p>
                            <p><strong>Sipariş Tarihi:</strong> {new Date(selectedOrder.createdAt).toLocaleDateString('tr-TR')}</p>
                            
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>İade Nedeni</Form.Label>
                                    <Form.Select 
                                        value={returnReason} 
                                        onChange={(e) => setReturnReason(e.target.value)}
                                        required
                                    >
                                        <option value="">Seçiniz</option>
                                        <option value="wrong_product">Yanlış ürün gönderildi</option>
                                        <option value="damaged">Ürün hasarlı/arızalı geldi</option>
                                        <option value="not_as_described">Ürün açıklamadaki gibi değil</option>
                                        <option value="size_issue">Ürün boyutu/ölçüsü uygun değil</option>
                                        <option value="changed_mind">Fikrim değişti</option>
                                        <option value="other">Diğer</option>
                                    </Form.Select>
                                </Form.Group>
                                
                                <Form.Group className="mb-3">
                                    <Form.Label>Açıklama</Form.Label>
                                    <Form.Control 
                                        as="textarea" 
                                        rows={3} 
                                        value={returnDescription}
                                        onChange={(e) => setReturnDescription(e.target.value)}
                                        placeholder="İade talebinizle ilgili detayları buraya yazabilirsiniz."
                                    />
                                </Form.Group>
                                
                                {/* Görsel Yükleme Alanı */}
                                <Form.Group className="mb-3">
                                    <Form.Label>Görseller (İsteğe Bağlı)</Form.Label>
                                    <div className="image-upload-container">
                                        <div className="image-upload-button">
                                            <Form.Control
                                                type="file"
                                                accept="image/jpeg, image/png, image/jpg, image/webp"
                                                onChange={handleImageUpload}
                                                multiple
                                                className="d-none"
                                                id="return-images-input"
                                                disabled={returnImages.length >= 5}
                                            />
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => document.getElementById('return-images-input').click()}
                                                disabled={returnImages.length >= 5}
                                            >
                                                <FontAwesomeIcon icon={faUpload} className="me-2" />
                                                Görsel Yükle
                                            </Button>
                                        </div>
                                        <small className="text-muted">
                                            En fazla 5 görsel yükleyebilirsiniz (JPEG, PNG, WebP). Her görsel maksimum 5MB olmalıdır.
                                        </small>
                                    </div>
                                    
                                    {/* Görsel Önizleme */}
                                    {previewImages.length > 0 && (
                                        <div className="image-previews mt-3">
                                            <Row>
                                                {previewImages.map((preview, index) => (
                                                    <Col key={index} xs={6} md={3} className="mb-3">
                                                        <div className="image-preview-container">
                                                            <img 
                                                                src={preview.url} 
                                                                alt={`Önizleme ${index + 1}`} 
                                                                className="img-thumbnail preview-image"
                                                            />
                                                            <Button 
                                                                variant="danger" 
                                                                size="sm" 
                                                                className="remove-image-btn"
                                                                onClick={() => removeImage(index)}
                                                            >
                                                                <FontAwesomeIcon icon={faTimes} />
                                                            </Button>
                                                        </div>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </div>
                                    )}
                                </Form.Group>
                            </Form>
                            
                            <div className="return-policy-info">
                                <h5>İade Politikası</h5>
                                <ul>
                                    <li>Ürünler, teslim tarihinden itibaren 14 gün içinde iade edilebilir.</li>
                                    <li>İade edilecek ürünler kullanılmamış, yıpranmamış ve orijinal ambalajında olmalıdır.</li>
                                    <li>Ürünün beğenilmemesi, yanlış model/renk seçimi veya müşteri kaynaklı sebeplerle yapılan iadelerde kargo ücreti müşteriye aittir.</li>
                                    <li>Hatalı, kusurlu veya yanlış gönderilen ürünlerin iadesinde kargo ücreti firmamız tarafından karşılanır.</li>
                                    <li>İade talebi onaylandıktan sonra, ürünün tarafımıza ulaşmasını takiben 14 gün içinde ödeme iadesi yapılır.</li>
                                    <li>Özel üretim, kişiselleştirilmiş ürünler iade edilemez.</li>
                                </ul>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowReturnModal(false)}>
                        İptal
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={submitReturnRequest}
                        disabled={returnProcessing || !returnReason}
                    >
                        {returnProcessing ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                İşleniyor...
                            </>
                        ) : 'İade Talebini Gönder'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Başarılı İade Talebi Modalı */}
            <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} size="lg">
                <Modal.Header closeButton className="bg-success text-white">
                    <Modal.Title>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        İade Talebiniz Alındı
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center mb-4">
                        <div className="success-check-circle">
                            <svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                                <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none" />
                                <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                            </svg>
                        </div>
                        <h4>İade talebiniz başarıyla oluşturuldu!</h4>
                    </div>
                    
                    <Alert variant="info">
                        <p className="mb-2"><strong>Önemli Bilgilendirme:</strong></p>
                        <p>Eğer iade talebiniz kabul edilirse, size bir iade kargo etiketi ve paketinizi nereye ve nasıl göndereceğiniz konusunda talimatlar göndereceğiz. İlk önce iade talebi olmadan gönderilen ürünler kabul edilmeyecektir.</p>
                    </Alert>
                    
                    <div className="mt-3">
                        <p>İade talebinizin durumunu "İade Taleplerim" sayfasından takip edebilirsiniz.</p>
                        <p>Talebiniz incelendikten sonra size bilgilendirme yapılacaktır.</p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSuccessModal(false)}>
                        Kapat
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={() => {
                            setShowSuccessModal(false);
                            navigate('/return-requests');
                        }}
                    >
                        <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />
                        İade Taleplerime Git
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Orders;